
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";

const MidCenturyModernADUDesignCountySantaClara = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Mid-Century Modern ADU Design & Construction in Santa Clara County",
    "description": "Custom Mid-Century Modern ADUs in Santa Clara County. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/mid-century-modern-adu-design-county-santa-clara"
  };

  return (
    <div className="min-h-screen bg-background">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="text-center py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white">
            Mid-Century Modern ADU Design & Construction in Santa Clara County
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-300 mb-8">
            Iconic Mid-Century Modern accessory dwelling units designed for Santa Clara County's innovative communities and tech-forward lifestyle.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Get Free Consultation
          </Button>
        </section>

        {/* Why Choose Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Why Choose Mid-Century Modern ADUs in Santa Clara County?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Silicon Valley Heritage</h3>
              <p className="text-gray-300">Mid-Century Modern design that reflects Santa Clara County's innovative spirit and architectural legacy.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Tech-Forward Living</h3>
              <p className="text-gray-300">Modern spaces designed for the county's tech professionals and forward-thinking communities.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">County-Wide Expertise</h3>
              <p className="text-gray-300">Exceptional value across all of Santa Clara County's diverse and dynamic real estate markets.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Mid-Century Modern ADU Features & Design Elements
          </h2>
          <div className="bg-white rounded-xl p-8 max-w-4xl mx-auto">
            <ul className="space-y-4 text-gray-800">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Floor-to-ceiling windows and clerestory lighting</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Flat or low-pitched rooflines</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Open floor plans with post-and-beam construction</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Natural materials: wood, stone, and steel</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Seamless integration with outdoor living spaces</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Santa Clara County-Specific Mid-Century Modern ADU Considerations
          </h2>
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8 max-w-4xl mx-auto">
            <p className="text-gray-300 mb-6">
              In Santa Clara County, Mid-Century Modern ADUs must comply with county-wide requirements:
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>County building standards and regulations</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Seismic and geological considerations</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Environmental protection requirements</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Local jurisdiction compatibility standards</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-6">
              Our team navigates Santa Clara County's requirements to deliver stunning Mid-Century Modern ADUs.
            </p>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Mid-Century Modern ADU Cost in Santa Clara County
          </h2>
          <div className="text-center">
            <Link to="/adu-calculator">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-6 px-12 rounded-xl shadow-lg text-xl">
                Use Our ADU Cost Calculator
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Ready to Build Your Mid-Century Modern ADU in Santa Clara County?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Let's discuss your Mid-Century Modern ADU project and create something timeless for your Santa Clara County property.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Book Free Consultation
          </Button>
        </section>

        {/* Internal Links */}
        <section className="py-16">
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
            <h3 className="text-2xl font-light text-white mb-6">Explore More ADU Options in Santa Clara County:</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/county-santa-clara-adu" className="text-[#FF7A00] hover:underline">
                General ADU Services in Santa Clara County
              </Link>
              <Link to="/adu-cost-county-santa-clara" className="text-[#FF7A00] hover:underline">
                ADU Cost in Santa Clara County
              </Link>
              <Link to="/adu-regulations-county-santa-clara" className="text-[#FF7A00] hover:underline">
                ADU Regulations in Santa Clara County
              </Link>
            </div>
            
            <h3 className="text-2xl font-light text-white mb-6">Other Mid-Century Modern ADU Locations:</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/mid-century-modern-adu-design-san-jose" className="text-[#FF7A00] hover:underline">
                Mid-Century Modern ADU San Jose
              </Link>
              <Link to="/mid-century-modern-adu-design-palo-alto" className="text-[#FF7A00] hover:underline">
                Mid-Century Modern ADU Palo Alto
              </Link>
              <Link to="/mid-century-modern-adu-design-sunnyvale" className="text-[#FF7A00] hover:underline">
                Mid-Century Modern ADU Sunnyvale
              </Link>
              <Link to="/mid-century-modern-adu-design-cupertino" className="text-[#FF7A00] hover:underline">
                Mid-Century Modern ADU Cupertino
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default MidCenturyModernADUDesignCountySantaClara;
